import { useEffect, useRef } from "react"
import { hasModKey, isTypingTarget } from "@/lib/platform"

export interface Hotkey {
  /**
   * Key combo: "mod+k", "mod+b", "?", "escape", or a two-key sequence "g s".
   * `mod` is ⌘ on macOS and Ctrl elsewhere.
   */
  combo: string
  handler: (e: KeyboardEvent) => void
  /** Also fire while typing in inputs (only sensible for mod-combos). */
  allowInInputs?: boolean
}

const SEQUENCE_TIMEOUT = 1000

function matchesCombo(e: KeyboardEvent, combo: string) {
  const parts = combo.toLowerCase().split("+")
  const key = parts.pop()!
  const needMod = parts.includes("mod")
  const needShift = parts.includes("shift")
  const needAlt = parts.includes("alt")
  if (needMod !== hasModKey(e)) return false
  if (needAlt !== e.altKey) return false
  if (needShift && !e.shiftKey) return false
  return e.key.toLowerCase() === key
}

/**
 * Global keyboard shortcuts. Single keys and sequences are ignored while the
 * user is typing; mod-combos work everywhere when `allowInInputs` is set.
 */
export function useHotkeys(hotkeys: Hotkey[], enabled = true) {
  const ref = useRef(hotkeys)
  ref.current = hotkeys

  useEffect(() => {
    if (!enabled) return
    let pending: string | null = null
    let timer: ReturnType<typeof setTimeout> | undefined

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return
      const typing = isTypingTarget(e.target)
      const key = e.key.toLowerCase()

      for (const hk of ref.current) {
        const combo = hk.combo.toLowerCase()
        if (combo.includes(" ")) {
          if (typing || e.metaKey || e.ctrlKey || e.altKey) continue
          const [first, second] = combo.split(" ")
          if (pending === first && key === second) {
            e.preventDefault()
            pending = null
            clearTimeout(timer)
            hk.handler(e)
            return
          }
          continue
        }
        if (typing && !hk.allowInInputs) continue
        if (matchesCombo(e, combo) || (combo === "?" && e.key === "?")) {
          e.preventDefault()
          hk.handler(e)
          return
        }
      }

      // remember a possible sequence starter
      if (!typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        const starters = ref.current.filter((h) => h.combo.includes(" ")).map((h) => h.combo.split(" ")[0])
        if (starters.includes(key)) {
          pending = key
          clearTimeout(timer)
          timer = setTimeout(() => {
            pending = null
          }, SEQUENCE_TIMEOUT)
        } else pending = null
      }
    }

    window.addEventListener("keydown", onKeyDown)
    return () => {
      window.removeEventListener("keydown", onKeyDown)
      clearTimeout(timer)
    }
  }, [enabled])
}
